import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { cn } from 'src/utils/className';
import { AreaChart } from './AreaChart';
import { BarChart } from './BarChart';
import { CampaignMetrics } from './CampaignMetrics';
import { ChartContainer } from './ChartContainer';

// Constants
const TIME_PERIOD_OPTIONS = [
  { value: 'all-time', label: 'All time' },
  { value: 'last-7-days', label: 'Last 7 days' },
  { value: 'last-30-days', label: 'Last 30 days' },
  { value: 'last-90-days', label: 'Last 90 days' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
];

const ACTION_OPTIONS = [
  { value: 'all-actions', label: 'All actions' },
  { value: 'scheduled-appointment', label: 'Scheduled an Appointment' },
  { value: 'transferred-call', label: 'Transferred call' },
];

const CHART_TITLE_OPTIONS = [
  'Call performance: across all outreach stages',
  'Email performance: across all outreach stages',
  'SMS performance: across all outreach stages',
  'Overall campaign performance',
  'Conversion funnel analysis',
];

// Mock data - TODO: Replace with real data when available
const MOCK_FUNNEL_DATA = {
  labels: [
    'Calls Attempted',
    'Calls Picked Up',
    'Engaged Conversations',
    'Successful Outcomes',
  ],
  datasets: [
    {
      label: 'Count',
      data: [129, 77, 38, 13],
      backgroundColor: [
        'rgba(99, 48, 219, 0.3)',
        'rgba(99, 48, 219, 0.5)',
        'rgba(99, 48, 219, 0.7)',
        'rgba(99, 48, 219, 0.9)',
      ],
      borderColor: 'rgba(99, 48, 219, 1)',
      borderWidth: 1,
      borderRadius: 4,
    },
  ],
};

const MOCK_AREA_DATA = {
  labels: [
    'Aug. 12',
    'Aug. 13',
    'Aug. 14',
    'Aug. 15',
    'Aug. 16',
    'Aug. 17',
    'Aug. 18',
    'Aug. 19',
    'Aug. 20',
    'Today',
  ],
  datasets: [
    {
      label: 'Calls Attempted',
      data: [0.5, 2.5, 1, 2.5, 1, 1.5, 2, 1.8, 1.2, 1.5],
      borderColor: 'rgba(99, 48, 219, 1)',
      backgroundColor: 'rgba(99, 48, 219, 0.9)',
      fill: true,
      tension: 0.4,
    },
    {
      label: 'Calls Picked Up',
      data: [2, 7, 2.5, 5.5, 3.5, 4, 6, 5.5, 4.5, 10],
      borderColor: 'rgba(99, 48, 219, 0.8)',
      backgroundColor: 'rgba(99, 48, 219, 0.7)',
      fill: true,
      tension: 0.4,
    },
    {
      label: 'Engaged Conversations',
      data: [4, 10.5, 6, 7.5, 5, 6.5, 8, 7.5, 6.5, 13],
      borderColor: 'rgba(99, 48, 219, 0.6)',
      backgroundColor: 'rgba(99, 48, 219, 0.5)',
      fill: true,
      tension: 0.4,
    },
    {
      label: 'Successful Outcomes',
      data: [12, 14.5, 7, 11, 8, 9.5, 12, 11.5, 10.5, 14],
      borderColor: 'rgba(99, 48, 219, 0.4)',
      backgroundColor: 'rgba(99, 48, 219, 0.3)',
      fill: true,
      tension: 0.4,
    },
  ],
};

// Chart configurations
const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: { display: false },
    legend: { display: false },
    tooltip: {
      callbacks: {
        afterLabel: (context: { dataIndex: number }) => {
          const conversions = ['60%', '50%', '33.3%', ''];
          const conversion = conversions[context.dataIndex];
          return conversion ? `Conversion: ${conversion}` : '';
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { maxRotation: 45, minRotation: 0 },
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(167, 163, 188, 0.1)' },
    },
  },
  elements: {
    bar: { borderSkipped: false },
  },
};

const AREA_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: { display: false },
    legend: { display: false },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      titleColor: '#211D2A',
      bodyColor: '#373342',
      borderColor: '#CCC9E1',
      borderWidth: 1,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#504C5C', font: { size: 12 } },
    },
    y: {
      beginAtZero: true,
      max: 20,
      grid: { color: 'rgba(167, 163, 188, 0.1)', drawBorder: false },
      ticks: { color: '#504C5C', font: { size: 12 }, stepSize: 5 },
    },
  },
  elements: {
    point: { radius: 0, hoverRadius: 4 },
    line: { borderWidth: 2 },
  },
  interaction: { intersect: false, mode: 'index' as const },
};

// Skeleton Components
export const CampaignMetricsSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
    <div className="lg:col-span-8">
      <div className="bg-neutral-0 rounded-lg shadow p-6 h-full">
        <div className="flex items-center h-full gap-16">
          <div className="text-left flex flex-col justify-center">
            <Skeleton height={32} width={60} className="mb-2" />
            <div className="flex items-center gap-2">
              <Skeleton circle height={20} width={20} />
              <Skeleton height={16} width={80} />
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="w-px h-12 bg-neutral-100"></div>
          </div>
          <div className="flex items-center justify-between flex-1">
            {[1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className="text-left flex flex-col justify-center"
              >
                <Skeleton height={32} width={40} className="mb-2" />
                <div className="flex items-center gap-2">
                  <Skeleton circle height={20} width={20} />
                  <Skeleton height={16} width={60} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    <div className="lg:col-span-2">
      <div className="bg-neutral-0 rounded-lg shadow p-6 h-full">
        <div className="text-left flex flex-col justify-center h-full">
          <Skeleton height={32} width={100} className="mb-2" />
          <Skeleton height={16} width={80} />
        </div>
      </div>
    </div>
  </div>
);

export const ChartContainerSkeleton = () => (
  <div className="bg-neutral-0 rounded-lg shadow">
    <div className="flex items-center justify-between px-6 py-4">
      <div className="flex items-center gap-2">
        <Skeleton height={24} width={300} />
        <Skeleton circle height={16} width={16} />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton height={32} width={120} />
        <Skeleton height={32} width={120} />
      </div>
    </div>
    <div className="w-full border-t border-neutral-100"></div>
    <div className="px-6 py-4">
      <Skeleton height={300} />
    </div>
    <div className="w-full border-t border-neutral-100"></div>
    <div className="px-6 py-4">
      <Skeleton height={200} />
    </div>
  </div>
);

// Types
type CampaignMetricsData = {
  totalContacts: number;
  queued: number;
  active: number;
  errored: number;
  exited: number;
};

interface CampaignAnalyticsSectionProps {
  metrics: CampaignMetricsData;
  startDate?: string;
  startDateTz?: string;
  loadingContacts: boolean;
  workflowStatsLoading: boolean;
}

export const CampaignAnalyticsSection = ({
  metrics,
  startDate,
  startDateTz,
  loadingContacts,
  workflowStatsLoading,
}: CampaignAnalyticsSectionProps) => {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('all-time');
  const [selectedAction, setSelectedAction] = useState('all-actions');
  const [selectedChartTitle, setSelectedChartTitle] = useState(0);
  const [isChartTitleDropdownOpen, setIsChartTitleDropdownOpen] =
    useState(false);

  const handleChartTitleSelect = (title: string) => {
    setSelectedChartTitle(CHART_TITLE_OPTIONS.indexOf(title));
    setIsChartTitleDropdownOpen(false);
  };

  return (
    <>
      <CampaignMetrics
        totalContacts={metrics.totalContacts}
        queued={metrics.queued}
        active={metrics.active}
        errored={metrics.errored}
        exited={metrics.exited}
        startDate={startDate}
        startDateTz={startDateTz}
        className={loadingContacts ? 'opacity-50' : ''}
      />

      {/* TODO: hidden because we don't have analytics yet */}
      <div
        className={cn(
          'bg-neutral-0 rounded-lg shadow hidden',
          workflowStatsLoading ? 'opacity-50' : '',
        )}
      >
        <ChartContainer
          title={CHART_TITLE_OPTIONS[selectedChartTitle]}
          titleOptions={CHART_TITLE_OPTIONS}
          selectedTitle={selectedChartTitle}
          onTitleChange={handleChartTitleSelect}
          isTitleDropdownOpen={isChartTitleDropdownOpen}
          onTitleDropdownToggle={() =>
            setIsChartTitleDropdownOpen(!isChartTitleDropdownOpen)
          }
          timePeriodOptions={TIME_PERIOD_OPTIONS}
          selectedTimePeriod={selectedTimePeriod}
          onTimePeriodChange={setSelectedTimePeriod}
          actionOptions={ACTION_OPTIONS}
          selectedAction={selectedAction}
          onActionChange={setSelectedAction}
        >
          <BarChart
            data={MOCK_FUNNEL_DATA}
            options={CHART_OPTIONS}
            conversionPercentages={['60%', '50%', '33.3%']}
          />
        </ChartContainer>

        <div className="w-full border-t border-neutral-100"></div>

        <div className="px-6 py-4">
          <AreaChart data={MOCK_AREA_DATA} options={AREA_CHART_OPTIONS} />
        </div>
      </div>
    </>
  );
};
